/**
 * The MOBILE app frame: theme root → 430px column → sticky header → screen
 * switch → bottom nav, plus every overlay mount point.
 *
 * This is the ported shell of `design/Games Tonight Mobile.dc.html` lines 33-95,
 * 778-793 and 1269-1273 (INVENTORY §1, §2, §10).
 *
 * HISTORY: this WAS `App.tsx` in full. Lane D0 split it out verbatim so
 * `App.tsx` could become the boot + breakpoint switch between this tree and
 * `desktop/DesktopApp.tsx` (INVENTORY-DESKTOP §10.3). The only edits in that
 * move were mechanical: the component is `MobileApp`, and the two design-time
 * constants `ACCENT` / `SCANLINES` arrive as props from `App.tsx` instead of
 * being module consts here. Nothing else changed — this tree is regression-free
 * by construction, and the desktop tree never imports from `screens/` or
 * `sheets/`.
 *
 * Frame rules that the screen lanes must not re-litigate:
 *  - `login` and `reset` render WITHOUT header or bottom nav. The gate is
 *    `headerVM().authed` (`me && screen !== 'login' && screen !== 'reset'`).
 *  - The screen switch is a single `screen` field. There is no router;
 *    `store/history.ts` mirrors nav depth into browser history so the OS back
 *    gesture closes one level instead of leaving the site.
 *  - Screens take NO props; they read the store and their own selector.
 *  - `Tooltip` and `ToastHost` are always mounted, exactly once, here.
 *  - Sheets are mounted here off the ui slice flags; each sheet closes itself.
 *
 * Subscription note: this component intentionally subscribes to the whole store
 * (`useQd()`), because `headerVM` reads across almost every slice. Screens
 * should subscribe narrowly instead.
 */

import { Avatar } from './components/Avatar.tsx';
import { QuarterIcon } from './components/QuarterIcon.tsx';
import { ToastHost } from './components/ToastHost.tsx';
import { Tooltip } from './components/Tooltip.tsx';
import { accentVars } from './components/theme.ts';
import type { Accent } from './components/theme.ts';
import { usePageScroll } from './lib/scroll.ts';
import type { Screen } from './lib/types.ts';
import { takeResumeScroll } from './store/resume.ts';
import Crew from './screens/Crew.tsx';
import Detail from './screens/Detail.tsx';
import Discover from './screens/Discover.tsx';
import Goty from './screens/Goty.tsx';
import Interests from './screens/Interests.tsx';
import Landing from './screens/Landing.tsx';
import Join from './screens/Join.tsx';
import Ask from './screens/Ask.tsx';
import Backlog from './screens/Backlog.tsx';
import Scores from './screens/Scores.tsx';
import Monitoring from './screens/Monitoring.tsx';
import Manage from './screens/Manage.tsx';
import Member from './screens/Member.tsx';
import Recs from './screens/Recs.tsx';
import Waiting from './screens/Waiting.tsx';
import StartCrew from './screens/StartCrew.tsx';
import Login from './screens/Login.tsx';
import Mine from './screens/Mine.tsx';
import Playtests from './screens/Playtests.tsx';
import Profile from './screens/Profile.tsx';
import Recover from './screens/Recover.tsx';
import Reset from './screens/Reset.tsx';
import Round from './screens/Round.tsx';
import Schedule, { SchedNight } from './screens/Schedule.tsx';
import SchedSheets from './sheets/SchedSheets.tsx';
import Tonight from './screens/Tonight.tsx';
import NominateSheet from './sheets/NominateSheet.tsx';
import RoundJoinCard from './sheets/RoundJoinCard.tsx';
import DragGhost from './sheets/DragGhost.tsx';
import CustomColorSheet from './sheets/CustomColorSheet.tsx';
import FiltersSheet from './sheets/FiltersSheet.tsx';
import FriendsSheet from './sheets/FriendsSheet.tsx';
import GotyPickSheet from './sheets/GotyPickSheet.tsx';
import CrewSheet from './sheets/CrewSheet.tsx';
import EmailSheet from './sheets/EmailSheet.tsx';
import OwnerSheet from './sheets/OwnerSheet.tsx';
import PasswordSheet from './sheets/PasswordSheet.tsx';
import PlayerCountSheet from './sheets/PlayerCountSheet.tsx';
import MoveSheet from './sheets/MoveSheet.tsx';
import PtAddSheet from './sheets/PtAddSheet.tsx';
import PtAskSheet from './sheets/PtAskSheet.tsx';
import PtBrowseSheet from './sheets/PtBrowseSheet.tsx';
import PtDetailSheet from './sheets/PtDetailSheet.tsx';
import PtSyncSheet from './sheets/PtSyncSheet.tsx';
import PtTrackedSheet from './sheets/PtTrackedSheet.tsx';
import ScoreSheet from './sheets/ScoreSheet.tsx';
import SendSheet from './sheets/SendSheet.tsx';
import TellMeWhenSheet from './sheets/TellMeWhenSheet.tsx';
import AddItSheet from './sheets/AddItSheet.tsx';
import CallItSheet from './sheets/CallItSheet.tsx';
import TrackSheet from './sheets/TrackSheet.tsx';
import WhatsNewSheet from './sheets/WhatsNewSheet.tsx';
import { overlayOpen } from './store/history.ts';
import { useQd } from './store/index.ts';
import { roundChipVM } from './store/selectors.round.ts';
import { headerVM, switcherVM } from './store/selectors.ts';
import './styles/app.css';

export interface MobileAppProps {
  /** design-time prop, owned by `App.tsx` (INVENTORY §10) */
  accent: Accent;
  /** design-time prop, owned by `App.tsx` */
  scanlines: boolean;
}

export function MobileApp({ accent, scanlines }: MobileAppProps) {
  const s = useQd();
  const h = headerVM(s);
  const sw = switcherVM(s);
  const rc = roundChipVM(s);

  // Tabs keep their scroll offset and get it back; a pushed `detail` always
  // opens at the top; an open overlay freezes the page underneath it. All three
  // live in `lib/scroll.ts` — the frame only says which view is on screen.
  usePageScroll(s.screen, s.detailId, overlayOpen(s), takeResumeScroll);

  return (
    <div
      style={{
        minHeight: '100dvh',
        background: 'var(--gt-bg)',
        color: 'var(--gt-text)',
        fontFamily: 'var(--gt-font-sans)',
        ...accentVars(accent),
      }}
    >
      <div
        style={{
          maxWidth: 430,
          margin: '0 auto',
          minHeight: '100dvh',
          borderLeft: '1px solid var(--gt-line-0)',
          borderRight: '1px solid var(--gt-line-0)',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {h.isLanding ? <Landing /> : null}
        {h.isStartCrew ? <StartCrew /> : null}
        {h.isJoin ? <Join /> : null}
        {h.isAsk ? <Ask /> : null}
        {h.isWaiting ? <Waiting /> : null}
        {h.isLogin ? <Login /> : null}
        {h.isRecover ? <Recover /> : null}
        {h.isReset ? <Reset /> : null}

        {h.authed ? (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            {/* Sticky header — prototype lines 85-95. Hidden while a full-screen
                page is up: the round page (3039), game detail, and MY GAMES'
                sub-pages MY INTERESTS and GOTY (DESIGN-CHANGELOG 2026-09-03),
                each of which draws its own bar with the way out. */}
            {h.hdrOn ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '0 8px 0 14px',
                height: 56,
                borderBottom: '1px solid var(--gt-line)',
                background: 'var(--gt-bg-hd)',
                position: 'sticky',
                top: 0,
                zIndex: 40,
              }}
            >
              {/* HOME — the coin alone, and the wordmark text is deliberately NOT here.
                  This row cannot shrink: everything in it is `flex:none` except the
                  spacer, so its width is a hard sum and anything over the column
                  spills out of the page and raises a horizontal scrollbar. The budget,
                  worst case (long crew name, round live):

                    22 padding + 25 coin + 128 crew chip + 3×44 chips + 5×6 gaps = 337

                  which clears the 360px narrowest phone. Putting `GAMES TONIGHT?` back
                  costs 127px more (Press Start 2P is 1em/glyph: 14 × 8.5px, plus the
                  9px gap) for 464 — over even the 430px column, so it overflowed on
                  every phone once the round chip claimed its 44px + gap. The coin is
                  still the whole tap target, so nothing but decoration was lost.
                  `design/Games Tonight Mobile.dc.html:55` still draws the wordmark; the
                  prototype canvas is exactly 430 so the overflow is invisible upstream.
                  Reconcile there once this is on prod. */}
              <div
                onClick={() => s.nav('tonight')}
                data-tip="GAMES TONIGHT?"
                aria-label="GAMES TONIGHT?"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  height: 56,
                  paddingRight: 4,
                  flex: 'none',
                }}
              >
                <QuarterIcon u={1.5} hover={false} />
              </div>
              {/* The crew chip rides beside the coin because this column has
                  no drawer to put a switcher in — its navigation is the bottom
                  tab bar. 44px so it is a real tap target on a 430px screen.
                  `design/Games Tonight Mobile.dc.html:56`.

                  It is also the row's ONLY give, on purpose: `0 1 auto` lets the
                  crew name ellipsis further when the budget above is somehow
                  exceeded again, so a future header addition degrades into a
                  shorter crew name instead of a page-wide horizontal scrollbar.
                  The name span already carries `overflow:hidden`, which is what
                  floors its automatic minimum size at 0 rather than min-content. */}
              {h.authed && sw.canOpen ? (
                <div
                  onClick={sw.open ? s.closeSw : s.openSw}
                  data-sw=""
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    height: 44,
                    padding: '0 9px',
                    marginLeft: 4,
                    border: '1px solid var(--gt-border-2)',
                    background: 'var(--gt-panel)',
                    cursor: 'pointer',
                    flex: '0 1 auto',
                    minWidth: 0,
                    boxSizing: 'border-box',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--gt-font-mono)',
                      fontSize: '10px',
                      letterSpacing: '.04em',
                      color: 'var(--gt-text)',
                      whiteSpace: 'nowrap',
                      maxWidth: '92px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {sw.lockupTxt}
                  </span>
                  <span
                    style={{ fontFamily: 'var(--gt-font-mono)', fontSize: '9px', color: 'var(--gt-muted)', lineHeight: 1 }}
                  >
                    ▾
                  </span>
                </div>
              ) : null}
              <div style={{ flex: 1 }} />
              {/* The wordless round chip (3039, `design/Games Tonight Mobile.dc.html`):
                  a 44px square wearing the coin, only while a round exists. */}
              {rc.rChipOn ? (
                <div
                  onClick={() => (rc.rChipAct === 'open' ? s.rOpen() : void s.rJoin())}
                  data-tip={rc.rChipTip}
                  data-tip-pos="r"
                  style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 44,
                    height: 44,
                    flex: 'none',
                    border: '1px solid var(--acc)',
                    background: rc.rChipBg,
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                  }}
                >
                  <QuarterIcon u={2} hover={false} />
                  {rc.rChipPulse ? (
                    <span
                      style={{
                        position: 'absolute',
                        top: -5,
                        right: -5,
                        width: 10,
                        height: 10,
                        background: 'var(--acc)',
                        border: '1px solid var(--gt-bg-hd)',
                        animation: 'gt-rpulse 1.1s steps(1) infinite',
                      }}
                    />
                  ) : null}
                </div>
              ) : null}
              {/* Prototype `openWhatsNew()` (line 1837 queue + line 922 gate) —
                  the ui slice owns it since P3c-04; App just calls it. */}
              <div
                onClick={() => s.openWhatsNew()}
                data-tip="WHAT'S NEW"
                data-tip-pos="r"
                style={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 44,
                  height: 44,
                  flex: 'none',
                  border: '1px solid ' + h.wnBd,
                  color: h.wnClr,
                  fontSize: 15,
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                }}
              >
                ◆
                {h.hasNew ? (
                  <span
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      minWidth: 17,
                      height: 17,
                      boxSizing: 'border-box',
                      background: 'var(--gt-info)',
                      color: 'var(--gt-ink)',
                      fontFamily: 'var(--gt-font-pixel)',
                      fontSize: 7,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 3px',
                    }}
                  >
                    {h.newCount}
                  </span>
                ) : null}
              </div>
              <div
                onClick={() => s.nav('profile')}
                style={{
                  width: 44,
                  height: 44,
                  flex: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Avatar
                  style={{ flex: 'none' }}
                  size={36}
                  fs={10}
                  ini={h.meIni}
                  bg={h.meHueBg}
                  hasAv={h.meHasAv}
                  avBg={h.meAvBg}
                />
              </div>
            </div>
            ) : null}

            {/* Screen switch — one `screen` field, no router (INVENTORY §1) */}
            {h.isMine ? <Mine /> : null}
            {h.isBacklog ? <Backlog /> : null}
            {h.isScores ? <Scores /> : null}
            {h.isRecs ? <Recs /> : null}
            {h.isMonitoring ? <Monitoring /> : null}
            {h.isManage ? <Manage /> : null}
            {h.isMember ? <Member /> : null}
            {h.isRound ? <Round /> : null}
            {/* SCHEDULE (3071): the month under the chrome; a night full screen. */}
            {h.isSchedule ? <Schedule /> : null}
            {h.isSchedDay ? <SchedNight /> : null}
            {h.isDiscover ? <Discover /> : null}
            {h.isTonight ? <Tonight /> : null}
            {h.isInterests ? <Interests /> : null}
            {h.isGoty ? <Goty /> : null}
            {h.isPlaytests ? <Playtests /> : null}
            {h.isDetail ? <Detail /> : null}
            {h.isCrew ? <Crew /> : null}
            {h.isProfile ? <Profile /> : null}

            {/* Bottom nav — prototype lines 778-786. Off with the header on the round page. */}
            {h.hdrOn ? (
            <>
            <div style={{ height: 82 }} />
            <div
              style={{
                position: 'fixed',
                left: '50%',
                transform: 'translateX(-50%)',
                bottom: 0,
                width: '100%',
                maxWidth: 430,
                zIndex: 50,
                display: 'flex',
                background: 'var(--gt-bg-hd)',
                borderTop: '1px solid var(--gt-border)',
                boxSizing: 'border-box',
                paddingBottom: 'env(safe-area-inset-bottom)',
              }}
            >
              {h.tabItems.map((t) => (
                <div
                  key={t.key}
                  onClick={() => s.nav(t.key as Screen)}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 6,
                    padding: '10px 2px 12px',
                    cursor: 'pointer',
                    borderTop: '2px solid ' + t.bd,
                    marginTop: -1,
                  }}
                >
                  {/* Drawn, not a font glyph (DESIGN-CHANGELOG 2026-09-16): 15px,
                      and the tab's own colour carries it through currentColor. */}
                  <svg
                    viewBox={t.vb}
                    style={{ width: 15, height: 15, display: 'block', flex: 'none', color: t.clr }}
                    shapeRendering="crispEdges"
                    aria-hidden="true"
                  >
                    <path d={t.d} fill="currentColor" />
                  </svg>
                  <span style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: '6.5px', color: t.clr }}>{t.l}</span>
                </div>
              ))}
            </div>
            </>
            ) : null}
            {/* The one-shot join card when someone starts a round (3039). */}
            <RoundJoinCard />
          </div>
        ) : null}

        {scanlines ? <div className="gt-scanlines" /> : null}

        {/* Overlays — z 80 · move 85 · scanlines 90 · toast 100 · tip 110 · ghost 120 */}
        {s.fltOpen ? <FiltersSheet /> : null}
        {s.sheet ? <MoveSheet /> : null}
        {s.wnOpen ? <WhatsNewSheet /> : null}
        {s.ptDet ? <PtDetailSheet /> : null}
        {s.ptAddOpen ? <PtAddSheet /> : null}
        {s.ptBrowseOpen ? <PtBrowseSheet /> : null}
        {s.ptTrackedOpen ? <PtTrackedSheet /> : null}
        {s.ptSyncOpen ? <PtSyncSheet /> : null}
        {/* Last of the playtest block: it is raised BY the detail sheet's primary,
            so it has to paint over it. `history.ts` registers it in the same order. */}
        {s.ptAsk ? <PtAskSheet /> : null}
        {s.gotyPickOpen ? <GotyPickSheet /> : null}
        {/* The phone's whole crew surface for a game — registered in
            `history.ts` so back closes it and the page under it freezes. */}
        {s.friendsOpen ? <FriendsSheet /> : null}
        {s.capOpen ? <PlayerCountSheet /> : null}
        {s.pwOpen ? <PasswordSheet /> : null}
        {s.emOpen ? <EmailSheet /> : null}
        {s.swOpen ? <CrewSheet /> : null}
        {s.ownModal ? <OwnerSheet /> : null}
        {s.ccOpen ? <CustomColorSheet /> : null}
        {/* The round's NOMINATE sheet (3039) — registered as `rSheet` in history.ts. */}
        {s.rSheet ? <NominateSheet /> : null}
        {/* MY GAMES' two sheets (3060) — `score` / `track` in history.ts, after `cc`. */}
        {s.scId ? <ScoreSheet /> : null}
        {s.trkOpen ? <TrackSheet /> : null}
        {/* RECOMMEND (3062) — `send` in history.ts, after `track`; z 85 like the move sheet. */}
        {s.sdId ? <SendSheet /> : null}
        {/* TELL ME WHEN (3063) — `wm` in history.ts, after `send`. */}
        {s.wmId ? <TellMeWhenSheet /> : null}
        {/* CALL IT (AA phase 5) — `ask` in history.ts, after `wm`. */}
        {s.askId ? <CallItSheet /> : null}
        {/* ADD IT (docs/ADD-IT.md) — `add` in history.ts, after `ask`. */}
        {s.addId ? <AddItSheet /> : null}
        {/* SCHEDULE (3071): NOMINATE and the game sheet — `schNom` / `schGame` in history.ts. */}
        <SchedSheets />
        {s.drag ? <DragGhost /> : null}

        <ToastHost />
        <Tooltip />
      </div>
    </div>
  );
}

export default MobileApp;
